import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, Autocomplete, TextField,
  Select, MenuItem, FormControl, InputLabel, Chip,
  IconButton, CircularProgress, Tooltip,
  LinearProgress, Slider,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  List, ListItem, ListItemText, ListItemSecondaryAction,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DeleteIcon from '@mui/icons-material/Delete';
import ShieldIcon from '@mui/icons-material/Shield';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import GrainIcon from '@mui/icons-material/Grain';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WaterIcon from '@mui/icons-material/Water';
import axios from 'axios';
import SEO from '../components/SEO';
import { usePokemonList } from '../hooks/usePokemonList';
import { getTypeColor } from '../utils/typeMatchups';
import typeChartRaw from '../data/type_chart.json';
import { useMovesList } from '../hooks/useMovesList';
import { useAbilitiesList } from '../hooks/useAbilitiesList';
import {
  NATURES, STAT_NAME_MAP, STAT_DISPLAY, STAT_ORDER,
  MAX_SP_PER_STAT, MAX_SP_TOTAL,
  calcStat, applyBoost,
  getTypeEffectiveness, getWeatherMultiplier, getTerrainMultiplier,
  getStabMultiplier, getAbilityAtkStatMultiplier,
  getAbilityDmgMultiplier, getAbilityDefMultiplier,
  getItemAtkMultiplier, getItemDefMultiplier,
  calculateDamageRolls, getKOInfo, findOptimalDefensiveSP,
} from '../utils/damageCalc';

// ── Strip metadata comment from type chart ────────────────────────────────────
const { _comment: _c, ...TYPE_CHART } = typeChartRaw;

// ── Static data ───────────────────────────────────────────────────────────────
const ALL_TYPES = [
  'Normal','Fire','Water','Electric','Grass','Ice','Fighting','Poison',
  'Ground','Flying','Psychic','Bug','Rock','Ghost','Dragon','Dark','Steel','Fairy',
];
const WEATHER_OPTIONS = ['None', 'Sun', 'Rain', 'Sand', 'Snow'];
const TERRAIN_OPTIONS = ['None', 'Electric', 'Grassy', 'Misty', 'Psychic'];
const STATUSES = ['Healthy', 'Burned', 'Paralyzed', 'Poisoned', 'Asleep', 'Frozen'];

const ITEMS_LIST = [
  'None',
  'Life Orb', 'Choice Band', 'Choice Specs', 'Choice Scarf',
  'Assault Vest', 'Eviolite', 'Expert Belt',
  'Muscle Band', 'Wise Glasses',
  'Focus Sash', 'Leftovers', 'Black Sludge', 'Rocky Helmet',
  'Booster Energy', 'Loaded Dice',
  'Silk Scarf','Charcoal','Mystic Water','Magnet','Miracle Seed',
  'NeverMeltIce','Black Belt','Sharp Beak','Poison Barb',
  'Soft Sand','Hard Stone','Silver Powder','Spell Tag','TwistedSpoon',
  'Dragon Fang','Black Glasses','Metal Coat','Fairy Feather',
];

const ABILITIES_ATTACKER = [
  'None',
  'Adaptability','Analytic','Beast Boost','Blaze','Competitive',
  'Defiant','Download','Flash Fire','Guts','Huge Power','Hustle',
  'Iron Fist','Neuroforce','Overgrow','Pure Power','Protosynthesis',
  'Quark Drive','Sheer Force','Solar Power','Swarm','Technician',
  'Torrent','Tough Claws','Tinted Lens','Stakeout','Supreme Overlord',
];
const ABILITIES_DEFENDER = [
  'None',
  'Filter','Flash Fire (defender)','Fur Coat','Intimidate','Levitate',
  'Lightning Rod','Marvel Scale','Motor Drive','Multiscale','Prism Armor',
  'Sap Sipper','Shadow Shield','Solid Rock','Storm Drain','Thick Fat',
  'Volt Absorb','Water Absorb','Wonder Guard',
];

// Type-boosting item → type
const TYPE_BOOST_ITEM = {
  'Silk Scarf':'normal','Charcoal':'fire','Mystic Water':'water',
  'Magnet':'electric','Miracle Seed':'grass','NeverMeltIce':'ice',
  'Black Belt':'fighting','Sharp Beak':'flying','Poison Barb':'poison',
  'Soft Sand':'ground','Hard Stone':'rock','Silver Powder':'bug',
  'Spell Tag':'ghost','TwistedSpoon':'psychic','Dragon Fang':'dragon',
  'Black Glasses':'dark','Metal Coat':'steel','Fairy Feather':'fairy',
};

const abilityFilter  = createFilterOptions({ limit: 10, stringify: s => s });
const generalFilter  = createFilterOptions({ limit: 50 });

function formatAbilityName(name) {
  if (!name) return 'None';
  return name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Ability immunities (defender side)
const ABILITY_IMMUNITIES = {
  'Levitate':'ground','Flash Fire (defender)':'fire',
  'Storm Drain':'water','Lightning Rod':'electric',
  'Motor Drive':'electric','Water Absorb':'water',
  'Volt Absorb':'electric','Sap Sipper':'grass',
};

// Shared toggle style for field condition buttons
const fieldToggleSx = (active) => ({
  display: 'flex', alignItems: 'center', gap: 0.5,
  px: 1, py: 0.35, borderRadius: 1, fontSize: 11, fontWeight: 600,
  cursor: 'pointer', userSelect: 'none', border: '1px solid',
  borderColor: active ? '#a5b4fc' : '#334155',
  backgroundColor: active ? '#221FC7' : 'transparent',
  color: active ? '#fff' : '#64748b',
  '&:hover': { borderColor: '#a5b4fc', color: '#a5b4fc' },
  transition: 'all 0.15s',
});

// Limit autocomplete list size to avoid rendering hundreds of options at once
const pokemonFilter = createFilterOptions({ limit: 15 });
const _moveFilterFn = createFilterOptions({ limit: 50, stringify: o => o.name });
// For large lists: show nothing until the user types, then show up to 50 matches
const moveFilter = (options, state) => {
  if (!state.inputValue) return [];
  return _moveFilterFn(options, state);
};

// ── Default state helpers ─────────────────────────────────────────────────────
const DEFAULT_SP  = { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 };
const DEFAULT_BOOST = { atk: 0, def: 0, spa: 0, spd: 0, spe: 0 };
const makePokemon = () => ({
  name: '', data: null, level: 50, nature: 'Hardy',
  ability: 'None', item: 'None', status: 'Healthy', teraType: null,
  statPoints: { ...DEFAULT_SP }, boosts: { ...DEFAULT_BOOST },
  baseOverrides: {}, grounded: true,
  currentHpPct: 100,
  reflect: false, lightScreen: false, auroraVeil: false, tailwind: false,
  moves: [{ ...DEFAULT_MOVE }],
  loading: false,
});

// Abilities that activate at ≤33% HP (attacker) or require full HP (defender)
const HP_DEPENDENT_ATK_ABILITIES = new Set(['Blaze', 'Torrent', 'Overgrow', 'Swarm']);
const HP_DEPENDENT_DEF_ABILITIES = new Set(['Multiscale', 'Shadow Shield']);
const DEFAULT_MOVE = {
  name: '', type: 'Normal', category: 'Physical', power: 80,
  isSpread: false, isContact: false, isPunch: false, criticalHit: false, helpingHand: false,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function computeFinalStats(pokemon) {
  if (!pokemon.data) return null;
  const out = {};
  pokemon.data.stats.forEach(s => {
    const key = STAT_NAME_MAP[s.name];
    if (!key) return;
    const base = pokemon.baseOverrides?.[key] ?? s.baseStat;
    out[`base_${key}`] = base;
    out[key] = calcStat(key, base, pokemon.level, pokemon.nature, pokemon.statPoints[key] ?? 0);
  });
  // Item stat modifiers (baked in so the Final column reflects them)
  const item = pokemon.item;
  if (item === 'Choice Scarf' && out.spe) out.spe = Math.floor(out.spe * 1.5);
  if (item === 'Choice Band'  && out.atk) out.atk = Math.floor(out.atk * 1.5);
  if (item === 'Choice Specs' && out.spa) out.spa = Math.floor(out.spa * 1.5);
  if (item === 'Assault Vest' && out.spd) out.spd = Math.floor(out.spd * 1.5);
  if (item === 'Eviolite') {
    if (out.def) out.def = Math.floor(out.def * 1.5);
    if (out.spd) out.spd = Math.floor(out.spd * 1.5);
  }
  // Status stat modifiers
  if (pokemon.status === 'Burned' && out.atk && pokemon.ability !== 'Guts')
    out.atk = Math.floor(out.atk * 0.5);
  if (pokemon.status === 'Paralyzed' && out.spe)
    out.spe = Math.floor(out.spe * 0.5);
  // Tailwind doubles Speed
  if (pokemon.tailwind && out.spe) out.spe = out.spe * 2;
  return out;
}

function getNatureColor(nature, statKey) {
  const nm = NATURES[nature]?.[statKey];
  if (nm === 1.1) return '#4ade80';  // green boost
  if (nm === 0.9) return '#f87171';  // red penalty
  return 'inherit';
}

const SORTED_NATURES = Object.keys(NATURES).sort((a, b) => a.localeCompare(b));

function getNatureModifiersLabel(nature) {
  const mods = NATURES[nature] || {};
  const boosted = Object.entries(mods).find(([stat, mult]) => stat !== 'hp' && mult > 1)?.[0];
  const lowered = Object.entries(mods).find(([stat, mult]) => stat !== 'hp' && mult < 1)?.[0];

  if (boosted && lowered) return `(+${STAT_DISPLAY[boosted]} -${STAT_DISPLAY[lowered]})`;
  if (boosted) return `(+${STAT_DISPLAY[boosted]})`;
  if (lowered) return `(-${STAT_DISPLAY[lowered]})`;
  return '(Neutral)';
}

function getNatureOptionLabel(nature) {
  return `${nature} ${getNatureModifiersLabel(nature)}`;
}

function typeEffLabel(eff) {
  if (eff === 0)   return { text: 'Immune',              color: '#9333ea' };
  if (eff < 1)     return { text: 'Not very effective…', color: '#ef4444' };
  if (eff === 1)   return { text: 'Neutral',             color: '#94a3b8' };
  if (eff === 2)   return { text: 'Super effective!',    color: '#22c55e' };
  if (eff >= 4)    return { text: 'Extremely effective!',color: '#ef4444' };
  return { text: '', color: '#94a3b8' };
}

function totalSP(sp) {
  return Object.values(sp).reduce((a, b) => a + b, 0);
}

// ── Damage calculation (standalone, used for each move) ──────────────────────
function calcMoveResult(attacker, defender, atkStats, defStats, move, { weather, terrain, helpingHand, attackerGrounded, defenderGrounded, gravity = false, magicRoom = false, wonderRoom = false, teraActive = false }) {
  if (!atkStats || !defStats) return null;
  if (!move.power || move.power <= 0 || move.category === 'Status') return null;

  const isPhysical = move.category === 'Physical';
  const isSpecial  = move.category === 'Special';

  // Gravity: all Pokémon are grounded regardless
  if (gravity) { attackerGrounded = true; defenderGrounded = true; }
  const mt = move.type?.toLowerCase();
  const defTypes = (teraActive && defender.teraType)
    ? [defender.teraType]
    : (defender.data?.types?.map(t => t.name) || []);
  const atkTypes = attacker.data?.types?.map(t => t.name) || [];

  const immuneType = ABILITY_IMMUNITIES[defender.ability];
  if (immuneType && immuneType === mt) return { immune: true };

  const typeEff = getTypeEffectiveness(move.type, defTypes, TYPE_CHART);
  if (typeEff === 0) return { immune: true, typeEff: 0 };

  const isFoulPlay  = move.name === 'Foul Play';
  const atkStatKey  = move.name === 'Body Press' ? 'def' : (isPhysical ? 'atk' : 'spa');
  const defStatKey  = (move.name === 'Psyshock' || move.name === 'Psystrike') ? 'def' : (isPhysical ? 'def' : 'spd');

  let atkBase = isFoulPlay ? defStats['atk'] : atkStats[atkStatKey];
  let defBase = defStats[defStatKey];

  atkBase = Math.floor(atkBase * getAbilityAtkStatMultiplier(attacker.ability, move.category, weather));

  if (weather === 'Sand' && isSpecial && defStatKey === 'spd' && defTypes.some(t => t.toLowerCase() === 'rock'))
    defBase = Math.floor(defBase * 1.5);
  if (weather === 'Snow' && isPhysical && defStatKey === 'def' && defTypes.some(t => t.toLowerCase() === 'ice'))
    defBase = Math.floor(defBase * 1.5);

  const atkBoost = isFoulPlay ? (defender.boosts['atk'] ?? 0) : (attacker.boosts[atkStatKey] ?? 0);
  const defBoost = defender.boosts[defStatKey] ?? 0;
  const atkStat  = applyBoost(atkBase, atkBoost);
  const defStat  = applyBoost(defBase, defBoost);

  const stab = getStabMultiplier(
    move.type,
    atkTypes,
    attacker.ability === 'Adaptability',
    teraActive ? attacker.teraType : null,
  );
  const weatherMult  = getWeatherMultiplier(weather, move.type);
  const terrainMult  = getTerrainMultiplier(terrain, move.type, attackerGrounded);

  // Magic Room disables held items
  let itemDmgMult = 1;
  if (!magicRoom) {
    if (attacker.item === 'Life Orb')  itemDmgMult = 1.3;
    else if (attacker.item === 'Muscle Band'  && isPhysical)  itemDmgMult = 1.1;
    else if (attacker.item === 'Wise Glasses' && isSpecial)   itemDmgMult = 1.1;
    else if (attacker.item === 'Expert Belt'  && typeEff > 1) itemDmgMult = 1.2;
    else if (TYPE_BOOST_ITEM[attacker.item] === mt)           itemDmgMult = 1.2;
  }
  if (helpingHand) itemDmgMult *= 1.5;

  const abilityDmgMult = getAbilityDmgMultiplier(attacker.ability, move.type, move.category, move.isContact, move.isPunch, typeEff, move.power, weather, attacker.currentHpPct ?? 100);
  const abilityDefMult = getAbilityDefMultiplier(defender.ability, move.category, move.type, typeEff, defenderGrounded, defender.currentHpPct ?? 100);

  // Screens: ×2/3 in doubles (crits ignore screens — handled inside calculateDamageRolls)
  let screenMult = 1;
  if (defender.auroraVeil) screenMult = 2 / 3;
  else if (isPhysical && defender.reflect) screenMult = 2 / 3;
  else if (isSpecial  && defender.lightScreen) screenMult = 2 / 3;

  // Wonder Room: swap Def ↔ SpD of defender
  // (already applied above via defStatKey swap if needed — handled via wonderRoom flag)
  const effectiveDefStat = wonderRoom
    ? applyBoost(defStats[isPhysical ? 'spd' : 'def'], defBoost)
    : defStat;

  console.log('[dmg]', { ability: attacker.ability, hpPct: attacker.currentHpPct, abilityDmgMult, abilityDefMult, atkStat, defStat: effectiveDefStat, stab, typeEff, level: attacker.level, power: move.power });

  const rolls = calculateDamageRolls({
    attackerLevel: attacker.level, movePower: move.power,
    moveCategory: move.category, moveType: move.type,
    atkStat, defStat: effectiveDefStat,
    isCritical: move.criticalHit,
    stabMultiplier: stab, typeEffectiveness: typeEff,
    weatherMultiplier: weatherMult, terrainMultiplier: terrainMult,
    burnMultiplier: 1, itemAtkMultiplier: itemDmgMult,
    abilityDmgMultiplier: abilityDmgMult, abilityDefMultiplier: abilityDefMult,
    screenMultiplier: screenMult,
    isSpread: move.isSpread,
  });

  const koInfo = getKOInfo(rolls, defStats.hp);

  // Build Showdown-style copy string
  const STAT_SHOWDOWN = { atk: 'Atk', spa: 'SpA', def: 'Def', spd: 'SpD' };
  const atkNature = NATURES[attacker.nature]?.[atkStatKey];
  const natureMod = atkNature === 1.1 ? '+' : atkNature === 0.9 ? '-' : '';
  const atkName = attacker.data?.displayName || attacker.name || 'Attacker';
  const defName = defender.data?.displayName || defender.name || 'Defender';
  const defStatLabel = STAT_SHOWDOWN[defStatKey] || 'Def';
  const koLabelLower = koInfo.koLabel.toLowerCase().replace('guaranteed ', 'guaranteed ');

  // Collect context tags for attacker side
  const atkTags = [];
  const atkBoostVal = isFoulPlay ? (defender.boosts[atkStatKey] ?? 0) : (attacker.boosts[atkStatKey] ?? 0);
  if (atkBoostVal > 0) atkTags.push(`+${atkBoostVal}`);
  else if (atkBoostVal < 0) atkTags.push(`${atkBoostVal}`);
  if (attacker.item && attacker.item !== 'None' && !magicRoom) {
    const itemMult = getItemAtkMultiplier(attacker.item, move.category, move.type);
    const expertBeltActive = attacker.item === 'Expert Belt' && typeEff > 1;
    if (itemMult > 1 || expertBeltActive) atkTags.push(attacker.item);
  }
  if (abilityDmgMult > 1 || getAbilityAtkStatMultiplier(attacker.ability, move.category, weather) > 1) {
    if (attacker.ability !== 'None') atkTags.push(attacker.ability);
  }
  if (move.helpingHand) atkTags.push('Helping Hand');
  if (move.criticalHit) atkTags.push('Crit');
  if (move.isSpread) atkTags.push('Spread');
  if (weatherMult > 1) atkTags.push(`${weather} boost`);
  else if (weatherMult < 1) atkTags.push(`${weather} penalty`);
  if (terrainMult > 1) atkTags.push(`${terrain} Terrain boost`);
  else if (terrainMult < 1) atkTags.push(`${terrain} Terrain penalty`);
  if (attacker.status === 'Burned' && move.category === 'Physical' && attacker.ability !== 'Guts')
    atkTags.push('Burn');

  // Collect context tags for defender side
  const defTags = [];
  const defBoostVal = defender.boosts[defStatKey] ?? 0;
  if (defBoostVal > 0) defTags.push(`+${defBoostVal}`);
  else if (defBoostVal < 0) defTags.push(`${defBoostVal}`);
  if (defender.item && defender.item !== 'None' && !magicRoom) {
    const defItemMult = getItemDefMultiplier(defender.item, move.category);
    if (defItemMult < 1) defTags.push(defender.item);
  }
  if (abilityDefMult < 1) defTags.push(defender.ability);
  if (screenMult < 1) {
    if (defender.auroraVeil) defTags.push('Aurora Veil');
    else if (move.category === 'Physical' && defender.reflect) defTags.push('Reflect');
    else if (move.category === 'Special' && defender.lightScreen) defTags.push('Light Screen');
  }
  // Weather defensive boost (Sand→Rock SpD, Snow→Ice Def)
  const defWeatherMult = (() => {
    if (weather === 'Sand' && move.category === 'Special' && defStatKey === 'spd' && defTypes.some(t => t.toLowerCase() === 'rock')) return 1.5;
    if (weather === 'Snow' && move.category === 'Physical' && defStatKey === 'def' && defTypes.some(t => t.toLowerCase() === 'ice')) return 1.5;
    return 1;
  })();
  if (defWeatherMult > 1) defTags.push(`${weather} SpD boost`);
  if (teraActive && defender.teraType) defTags.push(`Tera ${defender.teraType}`);

  // Deduplicate tags
  const atkTagsUniq = [...new Set(atkTags)];

  // Split attacker tags: item/ability go before the move name (@ Item), rest go in brackets after
  const isBoostStage = t => /^[+-]\d/.test(t);
  const isContextual  = t => t === 'Crit' || t === 'Helping Hand' || t === 'Spread' || t === 'Burn'
                           || t.includes('boost') || t.includes('penalty');
  const atkItemAbility = atkTagsUniq.filter(t => !isBoostStage(t) && !isContextual(t));
  const atkBoostStages = atkTagsUniq.filter(isBoostStage);
  const atkContextual  = atkTagsUniq.filter(isContextual);

  const boostPrefix   = atkBoostStages.length ? `${atkBoostStages[0]} ` : '';
  const itemSuffix    = atkItemAbility.length  ? ` @ ${atkItemAbility.join(' + ')}` : '';
  const contextSuffix = atkContextual.length   ? ` [${atkContextual.join(', ')}]`  : '';
  const defContext    = defTags.length ? ` (${defTags.join(', ')})` : '';

  const showdownText =
    `${boostPrefix}${atkBase}${natureMod} ${STAT_SHOWDOWN[atkStatKey]} ${atkName}${itemSuffix} ${move.name}${contextSuffix}` +
    ` vs. ${defStats.hp} HP / ${defBase} ${defStatLabel} ${defName}${defContext}: ` +
    `${rolls[0]}-${rolls[rolls.length - 1]} (${koInfo.minPct}% - ${koInfo.maxPct}%) -- ${koLabelLower}`;

  return { rolls, hpStat: defStats.hp, koInfo, typeEff, stab, showdownText };
}

// ── Compact editable number input ────────────────────────────────────────────
const numInputStyle = (color) => ({
  width: 30, textAlign: 'center', fontSize: 12,
  background: 'transparent', border: 'none',
  borderBottom: '1px solid #334155',
  color: color || '#e2e8f0', outline: 'none',
  padding: '1px 0', MozAppearance: 'textfield',
});

// SP input: slider + number input
function SpInput({ value, onChange }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%', px: 0.5 }}>
      <Slider
        size="small"
        min={0}
        max={MAX_SP_PER_STAT}
        value={value}
        onChange={(_, v) => onChange(v)}
        sx={{
          flex: 1,
          color: value > 0 ? '#a5b4fc' : '#334155',
          '& .MuiSlider-thumb': { width: 10, height: 10 },
          '& .MuiSlider-rail': { opacity: 0.3 },
        }}
      />
      <input
        type="number" min={0} max={MAX_SP_PER_STAT}
        value={value}
        onChange={e => onChange(parseInt(e.target.value, 10))}
        style={{ ...numInputStyle(value > 0 ? '#a5b4fc' : '#94a3b8'), width: 36, flexShrink: 0 }}
      />
    </Box>
  );
}

// Stage counter: − / colored value / +
function BoostCounter({ value, onDec, onInc }) {
  const color = value > 0 ? '#4ade80' : value < 0 ? '#f87171' : '#94a3b8';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <IconButton size="small" onClick={onDec} sx={{ p: '2px', color: '#94a3b8' }}>
        <RemoveIcon sx={{ fontSize: 11 }} />
      </IconButton>
      <Typography sx={{ minWidth: 22, textAlign: 'center', fontSize: 11, color, fontWeight: 700 }}>
        {value > 0 ? `+${value}` : value}
      </Typography>
      <IconButton size="small" onClick={onInc} sx={{ p: '2px', color: '#94a3b8' }}>
        <AddIcon sx={{ fontSize: 11 }} />
      </IconButton>
    </Box>
  );
}

// ── Result line (compact, inside MoveCard) ────────────────────────────────────
// ── Preset storage helpers ────────────────────────────────────────────────────
function presetsKey(pokemonName) {
  return `dc_presets_${(pokemonName || 'unknown').toLowerCase()}`;
}
function loadPresets(pokemonName) {
  try { return JSON.parse(localStorage.getItem(presetsKey(pokemonName)) || '[]'); }
  catch { return []; }
}
function savePreset(pokemonName, presetName, pokemon) {
  const presets = loadPresets(pokemonName);
  const slim = {
    nature: pokemon.nature,
    ability: pokemon.ability,
    item: pokemon.item,
    status: pokemon.status,
    teraType: pokemon.teraType,
    level: pokemon.level,
    statPoints: pokemon.statPoints,
    boosts: pokemon.boosts,
    baseOverrides: pokemon.baseOverrides,
    moves: pokemon.moves,
  };
  presets.push({ name: presetName, pokemon: slim, savedAt: Date.now() });
  try {
    localStorage.setItem(presetsKey(pokemonName), JSON.stringify(presets));
  } catch (e) {
    alert('No hay espacio suficiente en el almacenamiento local para guardar este preset.');
  }
}
function deletePreset(pokemonName, idx) {
  const presets = loadPresets(pokemonName);
  presets.splice(idx, 1);
  localStorage.setItem(presetsKey(pokemonName), JSON.stringify(presets));
}

// ── Save preset dialog ────────────────────────────────────────────────────────
function SavePresetDialog({ open, onClose, onSave, defaultName }) {
  const [name, setName] = useState('');
  const handleOpen = () => setName(defaultName || '');

  return (
    <Dialog open={open} onClose={onClose} TransitionProps={{ onEnter: handleOpen }}
      PaperProps={{ sx: { backgroundColor: '#1a1a3e', border: '1px solid #334155', minWidth: 300 } }}>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 700, color: '#a5b4fc', pb: 1 }}>
        Guardar preset
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TextField
          autoFocus fullWidth size="small"
          label="Nombre del preset"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && name.trim()) { onSave(name.trim()); onClose(); } }}
          inputProps={{ maxLength: 40 }}
          sx={{
            '& .MuiInputBase-input': { color: '#e2e8f0' },
            '& .MuiInputLabel-root': { color: '#94a3b8' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a5b4fc' },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
        <Button size="small" onClick={onClose} sx={{ color: '#64748b', fontSize: 12 }}>Cancelar</Button>
        <Button
          size="small" variant="contained" disabled={!name.trim()}
          onClick={() => { onSave(name.trim()); onClose(); }}
          sx={{ fontSize: 12, backgroundColor: '#221FC7', '&:hover': { backgroundColor: '#3730d4' } }}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Load preset dialog ────────────────────────────────────────────────────────
function LoadPresetDialog({ open, onClose, pokemonName, onLoad }) {
  const [presets, setPresets] = useState([]);

  const refresh = () => setPresets(loadPresets(pokemonName));

  const handleDelete = (idx) => {
    deletePreset(pokemonName, idx);
    refresh();
  };

  return (
    <Dialog open={open} onClose={onClose} TransitionProps={{ onEnter: refresh }}
      PaperProps={{ sx: { backgroundColor: '#1a1a3e', border: '1px solid #334155', minWidth: 360 } }}>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 700, color: '#a5b4fc', pb: 1 }}>
        Presets de {pokemonName || '—'}
      </DialogTitle>
      <DialogContent sx={{ pt: 0, px: 1 }}>
        {presets.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: '#64748b', px: 1, py: 1 }}>
            No hay presets guardados para este Pokémon.
          </Typography>
        ) : (
          <List dense disablePadding>
            {presets.map((p, i) => (
              <ListItem
                key={i}
                button
                onClick={() => { onLoad(p.pokemon); onClose(); }}
                sx={{
                  borderRadius: 1, mb: 0.5,
                  border: '1px solid #334155',
                  '&:hover': { backgroundColor: '#221FC744', borderColor: '#a5b4fc' },
                  pr: 5,
                }}
              >
                <ListItemText
                  primary={<Typography sx={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>{p.name}</Typography>}
                  secondary={
                    <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                      {p.pokemon.nature} · {p.pokemon.item !== 'None' ? p.pokemon.item : 'Sin objeto'} · {p.pokemon.moves.filter(m => m.name).map(m => m.name).join(', ') || 'Sin movimientos'}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(i); }}
                    sx={{ color: '#475569', '&:hover': { color: '#f87171' } }}>
                    <DeleteIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ color: '#64748b', fontSize: 12 }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function ResultLine({ result }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const { rolls, hpStat, koInfo, typeEff, stab, showdownText } = result;
  const koColor = koInfo.pct === 100 ? '#4ade80' : koInfo.pct > 0 ? '#f59e0b' : '#94a3b8';
  const effInfo = typeEff !== 1 ? typeEffLabel(typeEff) : null;

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!showdownText) return;
    navigator.clipboard.writeText(showdownText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Box>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setExpanded(e => !e)}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>
          {rolls[0]} – {rolls[rolls.length - 1]}
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#a5b4fc' }}>
          ({koInfo.minPct}% – {koInfo.maxPct}%)
        </Typography>
        <Typography sx={{ fontSize: 12, color: koColor, fontWeight: 700 }}>
          — {koInfo.koLabel}
        </Typography>
        {effInfo && (
          <Chip label={`${typeEff}×`} size="small"
            sx={{ height: 16, fontSize: 10, backgroundColor: effInfo.color + '33', color: effInfo.color, border: `1px solid ${effInfo.color}` }} />
        )}
        {stab > 1 && (
          <Chip label="STAB" size="small"
            sx={{ height: 16, fontSize: 10, backgroundColor: '#4ade8022', color: '#4ade80', border: '1px solid #4ade80' }} />
        )}
      </Box>
      {/* HP bar + copy button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
        <Box sx={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: '#1e293b', position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', inset: 0, width: `${Math.min(100, (rolls[0] / hpStat) * 100)}%`, backgroundColor: '#3b82f6', borderRadius: 3 }} />
          <Box sx={{ position: 'absolute', inset: 0, width: `${Math.min(100, (rolls[rolls.length - 1] / hpStat) * 100)}%`, backgroundColor: '#60a5fa', opacity: 0.4, borderRadius: 3 }} />
        </Box>
        <Tooltip title={copied ? 'Copied!' : 'Copy as text'} placement="top">
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              p: '2px', flexShrink: 0,
              color: copied ? '#4ade80' : '#475569',
              '&:hover': { color: copied ? '#4ade80' : '#a5b4fc' },
              transition: 'color 0.15s',
            }}
          >
            {copied
              ? <CheckIcon sx={{ fontSize: 13 }} />
              : <ContentCopyIcon sx={{ fontSize: 13 }} />}
          </IconButton>
        </Tooltip>
      </Box>
      {/* Rolls (expandable) */}
      {expanded && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.75 }}>
          {rolls.map((r, i) => (
            <Chip key={i} label={r} size="small" sx={{
              height: 18, fontSize: 10,
              backgroundColor: r >= hpStat ? '#166534' : '#1e293b',
              color: r >= hpStat ? '#4ade80' : '#64748b',
            }} />
          ))}
          <Typography sx={{ fontSize: 10, color: '#475569', alignSelf: 'center', ml: 0.5 }}>
            {koInfo.koCount}/16 KO
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ── Move card ─────────────────────────────────────────────────────────────────
function MoveCard({ move, result, canRemove, onUpdate, onRemove, onOptimizeDefense, movesList }) {
  const [optimizeMsg, setOptimizeMsg] = useState(null);

  const handleOptimize = () => {
    const err = onOptimizeDefense();
    if (err) {
      setOptimizeMsg({ text: err, error: true });
    } else {
      setOptimizeMsg({ text: 'SP aplicados', error: false });
    }
    setTimeout(() => setOptimizeMsg(null), 2500);
  };

  const handlePreset = (_, val) => {
    if (!val) { onUpdate('name', ''); return; }
    onUpdate('_preset', val);
  };

  return (
    <Paper sx={{ p: 1.25, mb: 0.75, backgroundColor: '#1a1a3e' }}>
      {/* Row 1: autocomplete + delete */}
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 0.75 }}>
        <Autocomplete
          options={movesList}
          getOptionLabel={o => o.name}
          value={movesList.find(m => m.name === move.name) || null}
          onChange={handlePreset}
          filterOptions={moveFilter}
          noOptionsText="Type to search moves…"
          size="small"
          sx={{ flex: 1 }}
          renderInput={params => <TextField {...params} label="Move" size="small" />}
          renderOption={(props, opt) => (
            <li {...props} key={opt.name}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Chip label={opt.type} size="small" sx={{ height: 14, fontSize: 9, backgroundColor: getTypeColor(opt.type), color: '#000' }} />
                <span style={{ fontSize: 12 }}>{opt.name}</span>
                {opt.power > 0 && <span style={{ fontSize: 10, color: '#94a3b8' }}>{opt.power}</span>}
              </Box>
            </li>
          )}
          isOptionEqualToValue={(o, v) => o.name === v.name}
        />
        {canRemove && (
          <Tooltip title="Remove move">
            <IconButton size="small" onClick={onRemove} sx={{ color: '#475569', '&:hover': { color: '#f87171' } }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Row 2 + Result: only shown when a move is selected */}
      {move.name && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: result ? 0.75 : 0 }}>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel sx={{ fontSize: 11 }}>Type</InputLabel>
              <Select value={move.type} label="Type" onChange={e => onUpdate('type', e.target.value)} sx={{ fontSize: 12 }}>
                {ALL_TYPES.map(t => (
                  <MenuItem key={t} value={t}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: getTypeColor(t) }} />
                      <span style={{ fontSize: 12 }}>{t}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 96 }}>
              <InputLabel sx={{ fontSize: 11 }}>Category</InputLabel>
              <Select value={move.category} label="Category" onChange={e => onUpdate('category', e.target.value)} sx={{ fontSize: 12 }}>
                {['Physical', 'Special', 'Status'].map(c => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField
              label="Power" type="number" size="small"
              value={move.power}
              onChange={e => onUpdate('power', Math.max(0, +e.target.value || 0))}
              inputProps={{ min: 0, max: 300 }}
              sx={{ width: 72 }}
            />

            {['criticalHit', 'isSpread', 'helpingHand'].map(flag => (
              <Box
                key={flag}
                onClick={() => onUpdate(flag, !move[flag])}
                sx={{
                  px: 1, py: 0.25, borderRadius: 1, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', userSelect: 'none',
                  border: '1px solid',
                  borderColor: move[flag] ? '#a5b4fc' : '#334155',
                  backgroundColor: move[flag] ? '#221FC7' : 'transparent',
                  color: move[flag] ? '#fff' : '#64748b',
                  '&:hover': { borderColor: '#a5b4fc', color: '#a5b4fc' },
                  transition: 'all 0.15s',
                }}
              >
                {flag === 'criticalHit' ? 'Crit' : flag === 'isSpread' ? 'Spread' : 'Helping Hand'}
              </Box>
            ))}
          </Box>

          {/* Result */}
          {result?.immune ? (
            <Typography sx={{ fontSize: 12, color: '#9333ea', fontWeight: 700 }}>
              Immune {result.typeEff === 0 ? '(type)' : '(ability)'}
            </Typography>
          ) : result?.rolls ? (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <Box sx={{ flex: 1 }}>
                <ResultLine result={result} />
              </Box>
              {onOptimizeDefense && (
                <Tooltip title="Calcular SP mínimos para aguantar este golpe" placement="top">
                  <IconButton
                    size="small"
                    onClick={handleOptimize}
                    sx={{
                      p: '3px', flexShrink: 0, mt: '1px',
                      color: '#475569',
                      '&:hover': { color: '#4ade80' },
                      transition: 'color 0.15s',
                    }}
                  >
                    <ShieldIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ) : null}

          {/* Optimize feedback */}
          {optimizeMsg && (
            <Typography sx={{ mt: 0.5, fontSize: 10, fontWeight: 600, color: optimizeMsg.error ? '#f87171' : '#4ade80' }}>
              {optimizeMsg.error ? '✗' : '✓'} {optimizeMsg.text}
            </Typography>
          )}

          {/* Move note */}
          {movesList.find(m => m.name === move.name)?.note && (
            <Typography sx={{ mt: 0.5, fontSize: 10, color: '#f59e0b', fontStyle: 'italic' }}>
              ⚠ {movesList.find(m => m.name === move.name).note}
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
}

// ── Move section (list of cards + add button) ─────────────────────────────────
function MoveSection({ moves, results, onAdd, onUpdate, onRemove, onOptimizeDefense, movesList }) {
  return (
    <Box sx={{ mt: 1 }}>
      {moves.map((mv, i) => (
        <MoveCard
          key={i}
          move={mv}
          result={results[i] ?? null}
          canRemove={true}
          onUpdate={(field, value) => onUpdate(i, field, value)}
          onRemove={() => onRemove(i)}
          onOptimizeDefense={onOptimizeDefense ? () => onOptimizeDefense(i) : undefined}
          movesList={movesList}
        />
      ))}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
        <Tooltip title="Add move">
          <IconButton
            onClick={onAdd}
            sx={{
              width: 28, height: 28,
              backgroundColor: '#2a2a5a',
              border: '1px dashed #475569',
              '&:hover': { backgroundColor: '#334155', borderColor: '#a5b4fc' },
            }}
          >
            <AddIcon sx={{ fontSize: 14, color: '#a5b4fc' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ── Pokemon Panel ─────────────────────────────────────────────────────────────
function PokemonPanel({ label, pokemon, finalStats, pokemonList, abilitiesList, onUpdate, onUpdateSP, onSetSP, onUpdateBoost, onUpdateBase, teraActive, onClear, onLoadPreset }) {
  const spTotal = totalSP(pokemon.statPoints);
  const spOver = spTotal >= MAX_SP_TOTAL;
  const [saveOpen, setSaveOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);

  const handleSave = (presetName) => {
    savePreset(pokemon.name, presetName, pokemon);
  };

  return (
    <Paper sx={{ p: 2, backgroundColor: '#2a2a5a' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#a5b4fc' }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Guardar preset" placement="top">
            <span>
              <IconButton
                size="small"
                onClick={() => setSaveOpen(true)}
                disabled={!pokemon.name}
                sx={{
                  p: '3px',
                  color: '#ffffff',
                  '&:hover': { color: '#f59e0b' },
                  '&.Mui-disabled': { color: '#1e293b' },
                  transition: 'color 0.15s',
                }}
              >
                <BookmarkIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cargar preset" placement="top">
            <span>
              <IconButton
                size="small"
                onClick={() => setLoadOpen(true)}
                disabled={!pokemon.name}
                sx={{
                  p: '3px',
                  color: '#ffffff',
                  '&:hover': { color: '#a5b4fc' },
                  '&.Mui-disabled': { color: '#1e293b' },
                  transition: 'color 0.15s',
                }}
              >
                <FolderOpenIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Box
            onClick={onClear}
            sx={{
              px: 1, py: 0.25, borderRadius: 1, fontSize: 11, fontWeight: 600,
              cursor: 'pointer', userSelect: 'none', border: '1px solid #334155',
              color: '#ffffff',
              '&:hover': { borderColor: '#f87171', color: '#f87171' },
              transition: 'all 0.15s',
            }}
          >
            Clear
          </Box>
        </Box>
      </Box>
      <SavePresetDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        onSave={handleSave}
        defaultName={pokemon.name}
      />
      <LoadPresetDialog
        open={loadOpen}
        onClose={() => setLoadOpen(false)}
        pokemonName={pokemon.name}
        onLoad={onLoadPreset}
      />

      {/* Pokemon Autocomplete */}
      <Autocomplete
        options={pokemonList}
        getOptionLabel={opt => opt.displayName || opt.name || ''}
        value={pokemonList.find(p => p.name === pokemon.name) || null}
        onChange={(_, val) => onUpdate('_fetch', val?.name || null)}
        filterOptions={pokemonFilter}
        loading={pokemon.loading}
        renderInput={params => (
          <TextField
            {...params}
            label="Pokémon"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {pokemon.loading && <CircularProgress color="inherit" size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, opt) => (
          <li {...props} key={opt.name}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(opt.sprite || opt.sprites?.default) && (
                <img src={opt.sprite || opt.sprites.default} alt={opt.name} style={{ width: 28, height: 28 }} />
              )}
              <span>{opt.displayName || opt.name}</span>
              {opt.types?.map(t => (
                <Chip key={t.name} label={t.name} size="small"
                  sx={{ height: 16, fontSize: 9, backgroundColor: getTypeColor(t.name), color: '#000' }} />
              ))}
            </Box>
          </li>
        )}
        sx={{ mb: 1.5 }}
        isOptionEqualToValue={(opt, val) => opt.name === val.name}
      />

      {/* Sprite + Types + Level + Nature in a row */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'flex-start' }}>
        {/* Sprite */}
        <Box sx={{ flexShrink: 0, width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {(pokemon.data?.officialArtwork || pokemon.data?.sprites?.officialArtwork) ? (
            <img
              src={pokemon.data.officialArtwork || pokemon.data.sprites.officialArtwork}
              alt={pokemon.data.displayName}
              style={{ width: 72, height: 72, objectFit: 'contain' }}
            />
          ) : (
            <Box sx={{ width: 72, height: 72, borderRadius: 2, backgroundColor: '#1a1a3e',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: 28 }}>?</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {/* Types */}
          {pokemon.data?.types && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {pokemon.data.types.map(t => (
                <Chip key={t.name} label={t.name} size="small"
                  sx={{ backgroundColor: getTypeColor(t.name), color: '#000', fontWeight: 700, height: 20, fontSize: 11 }} />
              ))}
            </Box>
          )}
          {/* Level */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: '#94a3b8', minWidth: 40 }}>Level</Typography>
            <TextField
              type="number" size="small" value={pokemon.level}
              onChange={e => onUpdate('level', Math.max(1, Math.min(100, +e.target.value || 50)))}
              inputProps={{ min: 1, max: 100 }}
              sx={{ width: 70, '& input': { py: 0.5, px: 1, fontSize: 13 } }}
            />
          </Box>
          {/* Nature */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: '#94a3b8', minWidth: 40 }}>Nature</Typography>
            <Autocomplete
              options={SORTED_NATURES}
              value={pokemon.nature}
              onChange={(_, val) => onUpdate('nature', val || 'Hardy')}
              getOptionLabel={opt => getNatureOptionLabel(opt)}
              filterOptions={generalFilter}
              size="small"
              disableClearable
              sx={{
                minWidth: { xs: 150, sm: 190 },
                maxWidth: '100%',
                flex: 1,
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  inputProps={{
                    ...params.inputProps,
                    'aria-label': 'Nature',
                  }}
                  sx={{ '& input': { fontSize: 12 } }}
                />
              )}
              renderOption={(props, opt) => (
                <li {...props} key={opt}>
                  <span style={{ fontSize: 12 }}>{getNatureOptionLabel(opt)}</span>
                </li>
              )}
              ListboxProps={{ sx: { fontSize: 12 } }}
            />
          </Box>
        </Box>
      </Box>

      {/* Ability + Item + Status */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 1.5 }}>
        <Autocomplete
          options={abilitiesList}
          value={pokemon.ability}
          onChange={(_, val) => onUpdate('ability', val || 'None')}
          filterOptions={abilityFilter}
          freeSolo
          size="small"
          renderInput={params => (
            <TextField
              {...params}
              label="Ability"
              size="small"
              onBlur={e => onUpdate('ability', e.target.value || 'None')}
            />
          )}
          ListboxProps={{ sx: { fontSize: 12 } }}
        />
        <Autocomplete
          options={ITEMS_LIST}
          value={pokemon.item}
          onChange={(_, val) => onUpdate('item', val || 'None')}
          filterOptions={generalFilter}
          size="small"
          fullWidth
          disableClearable
          renderInput={params => <TextField {...params} label="Item" size="small" />}
          ListboxProps={{ sx: { fontSize: 12 } }}
        />
        <Autocomplete
          options={STATUSES}
          value={pokemon.status}
          onChange={(_, val) => onUpdate('status', val || 'Healthy')}
          filterOptions={generalFilter}
          size="small"
          fullWidth
          disableClearable
          renderInput={params => <TextField {...params} label="Status" size="small" />}
          ListboxProps={{ sx: { fontSize: 12 } }}
        />
        {teraActive && (
          <Autocomplete
            options={['None', ...ALL_TYPES]}
            value={pokemon.teraType || 'None'}
            onChange={(_, val) => onUpdate('teraType', val === 'None' ? null : val || null)}
            filterOptions={generalFilter}
            size="small"
            fullWidth
            disableClearable
            renderInput={params => <TextField {...params} label="Tera Type" size="small" />}
            renderOption={(props, opt) => (
              <li {...props} key={opt}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {opt !== 'None' && (
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: getTypeColor(opt), flexShrink: 0 }} />
                  )}
                  <span style={{ fontSize: 12 }}>{opt}</span>
                </Box>
              </li>
            )}
            ListboxProps={{ sx: { fontSize: 12 } }}
          />
        )}
      </Box>

      {/* HP% slider — only shown when ability depends on current HP */}
      {(HP_DEPENDENT_ATK_ABILITIES.has(pokemon.ability) || HP_DEPENDENT_DEF_ABILITIES.has(pokemon.ability)) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, px: 0.5 }}>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', flexShrink: 0, minWidth: 52 }}>
            HP actual
          </Typography>
          <Slider
            size="small"
            min={1} max={100} value={pokemon.currentHpPct}
            onChange={(_, v) => onUpdate('currentHpPct', v)}
            sx={{
              flex: 1,
              color: pokemon.currentHpPct <= 33 ? '#f87171' : pokemon.currentHpPct <= 50 ? '#f59e0b' : '#4ade80',
              '& .MuiSlider-thumb': { width: 10, height: 10 },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            <input
              type="number" min={1} max={100}
              value={pokemon.currentHpPct}
              onChange={e => {
                const v = Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1));
                onUpdate('currentHpPct', v);
              }}
              style={{
                ...numInputStyle(pokemon.currentHpPct <= 33 ? '#f87171' : pokemon.currentHpPct <= 50 ? '#f59e0b' : '#4ade80'),
                width: 36,
              }}
            />
            <Typography sx={{ fontSize: 11, color: '#64748b' }}>%</Typography>
            {HP_DEPENDENT_ATK_ABILITIES.has(pokemon.ability) && pokemon.currentHpPct <= 33 && (
              <Typography sx={{ fontSize: 9, color: '#f87171', ml: 0.25 }}>●</Typography>
            )}
            {HP_DEPENDENT_DEF_ABILITIES.has(pokemon.ability) && pokemon.currentHpPct === 100 && (
              <Typography sx={{ fontSize: 9, color: '#4ade80', ml: 0.25 }}>●</Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Stat Table */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography sx={{ fontSize: 12, color: '#a5b4fc', fontWeight: 600 }}>
            Stat Points (IVs: 31 fixed)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              onClick={() => onUpdate('grounded', !pokemon.grounded)}
              sx={{
                px: 1, py: 0.25, borderRadius: 1, fontSize: 10, fontWeight: 600,
                cursor: 'pointer', userSelect: 'none',
                border: '1px solid',
                borderColor: pokemon.grounded ? '#a5b4fc' : '#334155',
                backgroundColor: pokemon.grounded ? '#221FC7' : 'transparent',
                color: pokemon.grounded ? '#fff' : '#64748b',
                '&:hover': { borderColor: '#a5b4fc', color: '#a5b4fc' },
                transition: 'all 0.15s',
              }}
            >
              Grounded
            </Box>
            <Typography sx={{ fontSize: 11, color: spOver ? '#f87171' : '#94a3b8' }}>
              {spTotal}/{MAX_SP_TOTAL}
            </Typography>
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, (spTotal / MAX_SP_TOTAL) * 100)}
          sx={{
            mb: 0.75, height: 4, borderRadius: 2,
            backgroundColor: '#1a1a3e',
            '& .MuiLinearProgress-bar': {
              backgroundColor: spOver ? '#f87171' : spTotal > 50 ? '#f59e0b' : '#4ade80',
            },
          }}
        />

        {/* Header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '26px 44px 1fr 56px 32px', gap: 0.5, mb: 0.25, px: 0.25 }}>
          {['Stat','Base','SP','Stage','Final'].map((h, i) => (
            <Typography key={h} sx={{ fontSize: 10, color: '#475569', textAlign: 'center',
              ...(i === 0 && { textAlign: 'left' }) }}>
              {h}
            </Typography>
          ))}
        </Box>

        {/* Stat rows */}
        {STAT_ORDER.map(stat => {
          const rawBase = finalStats?.[`base_${stat}`];
          const displayBase = pokemon.baseOverrides?.[stat] ?? rawBase ?? '';
          const boost = pokemon.boosts[stat] ?? 0;
          const rawFinal = finalStats?.[stat];
          const final = rawFinal != null
            ? (stat !== 'hp' && boost !== 0 ? applyBoost(rawFinal, boost) : rawFinal)
            : '—';
          const sp = pokemon.statPoints[stat] ?? 0;
          const natureColor = stat !== 'hp' ? getNatureColor(pokemon.nature, stat) : 'inherit';
          return (
            <Box key={stat} sx={{
              display: 'grid', gridTemplateColumns: '26px 44px minmax(0, 1fr) 56px 32px',
              gap: 0.5, alignItems: 'center', mb: 0.25, px: 0.25,
            }}>
              {/* Stat label */}
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: natureColor }}>
                {STAT_DISPLAY[stat]}
              </Typography>

              {/* Base (editable) */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <input
                  type="number" min={1} max={255}
                  value={displayBase}
                  onChange={e => onUpdateBase(stat, parseInt(e.target.value, 10))}
                  disabled={!finalStats}
                  style={{ ...numInputStyle('#94a3b8'), width: 38, borderBottomColor: '#334155' }}
                />
              </Box>

              {/* SP */}
              <SpInput
                value={sp}
                onChange={v => onSetSP(stat, v)}
              />

              {/* Stage (not for HP) */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {stat !== 'hp' ? (
                  <BoostCounter
                    value={boost}
                    onDec={() => onUpdateBoost(stat, -1)}
                    onInc={() => onUpdateBoost(stat, 1)}
                  />
                ) : (
                  <Typography sx={{ fontSize: 10, color: '#334155', textAlign: 'center' }}>—</Typography>
                )}
              </Box>

              {/* Final */}
              <Typography sx={{ fontSize: 13, textAlign: 'center', fontWeight: 700, color: natureColor }}>
                {final}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const DamageCalculatorPage = () => {
  const [p1, setP1] = useState(makePokemon);
  const [p2, setP2] = useState(makePokemon);
  const [weather, setWeather] = useState('None');
  const [terrain, setTerrain] = useState('None');
  const [gravity, setGravity] = useState(false);
  const [magicRoom, setMagicRoom] = useState(false);
  const [wonderRoom, setWonderRoom] = useState(false);
  const [teraActive, setTeraActive] = useState(false);

  const { pokemonList } = usePokemonList({ limit: 2000 });
  const abilitiesList = useAbilitiesList();
  const { moves: movesList } = useMovesList();

  const handleFetchPokemon = useCallback(async (side, name) => {
    const setter = side === 'p1' ? setP1 : setP2;
    if (!name) { setter(makePokemon); return; }
    setter(prev => ({ ...prev, loading: true, name }));
    try {
      const { data } = await axios.get(`/api/pokemon/${encodeURIComponent(name.toLowerCase())}`);
      setter(prev => ({
        ...prev, loading: false, data, name,
        ability: formatAbilityName(data.abilities?.[0]?.name),
        statPoints: { ...DEFAULT_SP }, boosts: { ...DEFAULT_BOOST },
        baseOverrides: {}, moves: [{ ...DEFAULT_MOVE }], currentHpPct: 100,
      }));
    } catch {
      setter(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const updatePokemon = useCallback((side, field, value) => {
    const setter = side === 'p1' ? setP1 : setP2;
    if (field === '_fetch') { handleFetchPokemon(side, value); return; }
    setter(prev => ({ ...prev, [field]: value }));
  }, [handleFetchPokemon]);

  const updateSP = useCallback((side, stat, delta) => {
    const setter = side === 'p1' ? setP1 : setP2;
    setter(prev => {
      const cur = prev.statPoints[stat];
      const next = Math.max(0, Math.min(MAX_SP_PER_STAT, cur + delta));
      if (totalSP(prev.statPoints) - cur + next > MAX_SP_TOTAL) return prev;
      return { ...prev, statPoints: { ...prev.statPoints, [stat]: next } };
    });
  }, []);

  const setSPDirect = useCallback((side, stat, value) => {
    const setter = side === 'p1' ? setP1 : setP2;
    setter(prev => {
      const clamped = Math.max(0, Math.min(MAX_SP_PER_STAT, isNaN(value) ? 0 : value));
      const available = MAX_SP_TOTAL - totalSP(prev.statPoints) + (prev.statPoints[stat] ?? 0);
      return { ...prev, statPoints: { ...prev.statPoints, [stat]: Math.min(clamped, available) } };
    });
  }, []);

  const updateBase = useCallback((side, stat, value) => {
    const setter = side === 'p1' ? setP1 : setP2;
    setter(prev => ({
      ...prev,
      baseOverrides: { ...prev.baseOverrides, [stat]: Math.max(1, Math.min(255, isNaN(value) ? 1 : value)) },
    }));
  }, []);

  const updateBoost = useCallback((side, stat, delta) => {
    const setter = side === 'p1' ? setP1 : setP2;
    setter(prev => ({
      ...prev, boosts: { ...prev.boosts, [stat]: Math.max(-6, Math.min(6, (prev.boosts[stat] ?? 0) + delta)) },
    }));
  }, []);

  // Move callbacks (per pokemon)
  const addMove = useCallback((side) => {
    const setter = side === 'p1' ? setP1 : setP2;
    setter(prev => ({ ...prev, moves: [...prev.moves, { ...DEFAULT_MOVE }] }));
  }, []);

  const updateMove = useCallback((side, idx, field, value) => {
    const setter = side === 'p1' ? setP1 : setP2;
    setter(prev => {
      const moves = [...prev.moves];
      if (field === '_preset') {
        moves[idx] = {
          ...DEFAULT_MOVE, name: value.name, type: value.type,
          category: value.category, power: value.power,
          isSpread: !!value.isSpread, isContact: !!value.isContact, isPunch: !!value.isPunch,
        };
      } else {
        moves[idx] = { ...moves[idx], [field]: value };
      }
      return { ...prev, moves };
    });
  }, []);

  const clearPokemon = useCallback((side) => {
    const setter = side === 'p1' ? setP1 : setP2;
    setter(makePokemon());
  }, []);

  const removeMove = useCallback((side, idx) => {
    const setter = side === 'p1' ? setP1 : setP2;
    setter(prev => {
      const moves = prev.moves.filter((_, i) => i !== idx);
      return { ...prev, moves: moves.length ? moves : [{ ...DEFAULT_MOVE }] };
    });
  }, []);

  const loadPreset = useCallback((side, savedPokemon) => {
    const setter = side === 'p1' ? setP1 : setP2;
    setter(prev => ({
      ...prev,
      nature: savedPokemon.nature ?? prev.nature,
      ability: savedPokemon.ability ?? prev.ability,
      item: savedPokemon.item ?? prev.item,
      status: savedPokemon.status ?? prev.status,
      teraType: savedPokemon.teraType ?? prev.teraType,
      statPoints: savedPokemon.statPoints ?? prev.statPoints,
      boosts: savedPokemon.boosts ?? prev.boosts,
      baseOverrides: savedPokemon.baseOverrides ?? prev.baseOverrides,
      moves: savedPokemon.moves ?? prev.moves,
    }));
  }, []);

  // Final stats
  const p1Stats = useMemo(() => computeFinalStats(p1), [p1]);
  const p2Stats = useMemo(() => computeFinalStats(p2), [p2]);

  // Damage results per direction
  const conditions = useMemo(() => ({ weather, terrain, gravity, magicRoom, wonderRoom, teraActive }), [weather, terrain, gravity, magicRoom, wonderRoom, teraActive]);

  const results1 = useMemo(() => {
    if (!p1Stats || !p2Stats) return [];
    return p1.moves.map(mv => calcMoveResult(p1, p2, p1Stats, p2Stats, mv,
      { ...conditions, helpingHand: mv.helpingHand, attackerGrounded: p1.grounded, defenderGrounded: p2.grounded }));
  }, [p1, p2, p1Stats, p2Stats, conditions]);

  const results2 = useMemo(() => {
    if (!p1Stats || !p2Stats) return [];
    return p2.moves.map(mv => calcMoveResult(p2, p1, p2Stats, p1Stats, mv,
      { ...conditions, helpingHand: mv.helpingHand, attackerGrounded: p2.grounded, defenderGrounded: p1.grounded }));
  }, [p1, p2, p1Stats, p2Stats, conditions]);

  const optimizeDefense = useCallback((attackerSide, moveIdx) => {
    const attacker = attackerSide === 'p1' ? p1 : p2;
    const defender = attackerSide === 'p1' ? p2 : p1;
    const atkStats  = attackerSide === 'p1' ? p1Stats : p2Stats;
    const defSetter = attackerSide === 'p1' ? setP2 : setP1;

    if (!atkStats || !defender.data) return 'Faltan datos del defensor';

    const move = attacker.moves[moveIdx];
    if (!move?.name || !move.power || move.power <= 0 || move.category === 'Status')
      return 'El movimiento no hace daño';

    const defTypes = (teraActive && defender.teraType)
      ? [defender.teraType]
      : (defender.data.types?.map(t => t.name) || []);
    const atkTypes = attacker.data?.types?.map(t => t.name) || [];

    const isFoulPlay = move.name === 'Foul Play';
    const isPhysical = move.category === 'Physical';
    const atkStatKey = move.name === 'Body Press' ? 'def' : (isPhysical ? 'atk' : 'spa');
    const defStatKey = (move.name === 'Psyshock' || move.name === 'Psystrike') ? 'def' : (isPhysical ? 'def' : 'spd');

    const defStats0 = computeFinalStats({ ...defender, statPoints: { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 } });
    if (!defStats0) return;

    let atkBase = isFoulPlay ? defStats0[atkStatKey] : atkStats[atkStatKey];
    atkBase = Math.floor(atkBase * getAbilityAtkStatMultiplier(attacker.ability, move.category, weather));

    let weatherDefMult = 1;
    if (weather === 'Sand' && move.category === 'Special' && defStatKey === 'spd' && defTypes.some(t => t.toLowerCase() === 'rock'))
      weatherDefMult = 1.5;
    if (weather === 'Snow' && isPhysical && defStatKey === 'def' && defTypes.some(t => t.toLowerCase() === 'ice'))
      weatherDefMult = 1.5;

    const atkBoost = isFoulPlay ? (defender.boosts[atkStatKey] ?? 0) : (attacker.boosts[atkStatKey] ?? 0);
    const atkStat  = Math.max(1, Math.floor(atkBase * (atkBoost >= 0 ? (2 + atkBoost) / 2 : 2 / (2 + Math.abs(atkBoost)))));

    const stab = getStabMultiplier(move.type, atkTypes, attacker.ability === 'Adaptability', teraActive ? attacker.teraType : null);
    const typeEff = getTypeEffectiveness(move.type, defTypes, TYPE_CHART);
    if (typeEff === 0) return 'El defensor es inmune a este tipo';

    const weatherMult = getWeatherMultiplier(weather, move.type);
    const terrainMult = getTerrainMultiplier(terrain, move.type, attacker.grounded);

    let itemDmgMult = 1;
    if (!magicRoom) {
      if (attacker.item === 'Life Orb') itemDmgMult = 1.3;
      else if (attacker.item === 'Muscle Band' && isPhysical) itemDmgMult = 1.1;
      else if (attacker.item === 'Wise Glasses' && move.category === 'Special') itemDmgMult = 1.1;
      else if (attacker.item === 'Expert Belt' && typeEff > 1) itemDmgMult = 1.2;
    }
    if (move.helpingHand) itemDmgMult *= 1.5;

    const abilityDmgMult = getAbilityDmgMultiplier(attacker.ability, move.type, move.category, move.isContact, move.isPunch, typeEff, move.power, weather);
    const abilityDefMult = getAbilityDefMultiplier(defender.ability, move.category, move.type, typeEff, defender.grounded);

    let screenMult = 1;
    if (defender.auroraVeil) screenMult = 2 / 3;
    else if (isPhysical && defender.reflect) screenMult = 2 / 3;
    else if (!isPhysical && defender.lightScreen) screenMult = 2 / 3;

    // Item multiplier on the defensive stat (e.g. Assault Vest on SpD, Eviolite on Def+SpD)
    let itemDefStatMult = 1;
    if (!magicRoom) {
      if (defender.item === 'Assault Vest' && defStatKey === 'spd') itemDefStatMult = 1.5;
      if (defender.item === 'Eviolite') itemDefStatMult = 1.5;
    }

    const defBoost = defender.boosts[defStatKey] ?? 0;
    const defBaseStatValue = defender.data.stats.find(s => STAT_NAME_MAP[s.name] === defStatKey)?.baseStat ?? 1;
    const baseHP = defender.data.stats.find(s => STAT_NAME_MAP[s.name] === 'hp')?.baseStat ?? 1;

    // SP already spent on other stats (not hp, not defStatKey)
    const currentSPOthers = Object.entries(defender.statPoints)
      .filter(([k]) => k !== 'hp' && k !== defStatKey)
      .reduce((sum, [, v]) => sum + v, 0);

    const result = findOptimalDefensiveSP({
      baseHP, baseDefStat: defBaseStatValue, defStatKey,
      defNature: defender.nature, defLevel: defender.level,
      currentSPOthers,
      itemDefMult: itemDefStatMult * weatherDefMult,
      defBoost,
      attackerLevel: attacker.level, movePower: move.power,
      moveCategory: move.category, moveType: move.type,
      atkStat,
      isCritical: move.criticalHit,
      stabMultiplier: stab, typeEffectiveness: typeEff,
      weatherMultiplier: weatherMult, terrainMultiplier: terrainMult,
      itemAtkMultiplier: itemDmgMult,
      abilityDmgMultiplier: abilityDmgMult, abilityDefMultiplier: abilityDefMult,
      screenMultiplier: screenMult, isSpread: move.isSpread,
    });

    if (!result) return 'Imposible aguantar el golpe con los SP disponibles';

    defSetter(prev => ({
      ...prev,
      statPoints: {
        ...prev.statPoints,
        hp: result.hpSP,
        [defStatKey]: result.defSP,
      },
    }));
    return null;
  }, [p1, p2, p1Stats, p2Stats, weather, terrain, magicRoom, teraActive]);

  // ── Render ──────────────────────────────────────────────────────────────────
  const weatherIcons = { Sun: <WbSunnyIcon/>, Rain: <WaterIcon/>, Sand: <GrainIcon/>, Snow: <AcUnitIcon/> };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1400, mx: 'auto', overflowX: 'hidden' }}>
      <SEO
        title="Damage Calculator — Pokémon Champions (No IVs, Stat Points)"
        description="The first damage calculator for Pokémon Champions. Calculates damage using the new Stat Points system (max 66 total, max 32 per stat) replacing EVs, with IVs always set to 31. Perfect for competitive VGC players."
        keywords="Pokemon Champions damage calculator, stat points calculator, Pokemon VGC damage calc, competitive Pokemon Champions, no IVs calculator, Pokemon damage calculator 2025"
        structuredData={[{
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'Pokémon Champions Damage Calculator',
          applicationCategory: 'GameApplication',
          description: 'Damage calculator for Pokémon Champions with the new Stat Points system. IVs are always 31, EVs replaced by Stat Points (max 66 total, max 32 per stat, 1:1 ratio).',
          url: 'https://traineracademy.xyz/damage-calculator',
          operatingSystem: 'Web',
        }]}
      />

      {/* Title */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>Damage Calculator</Typography>
        <Typography variant="body2" sx={{ color: '#a5b4fc' }}>
          Pokémon Champions edition — IVs always 31 · Stat Points replace EVs (max 66 total, max 32/stat)
        </Typography>
      </Box>

      {/* 3-column layout: P1 | Field | P2 */}
      <Grid container spacing={2} alignItems="flex-start">

        {/* Pokémon 1 */}
        <Grid item xs={12} sm={6} lg={5}>
          <PokemonPanel
            label="Pokémon 1"
            pokemon={p1} finalStats={p1Stats} pokemonList={pokemonList} abilitiesList={abilitiesList}
            onUpdate={(f, v) => updatePokemon('p1', f, v)}
            onUpdateSP={(s, d) => updateSP('p1', s, d)}
            onSetSP={(s, v) => setSPDirect('p1', s, v)}
            onUpdateBoost={(s, d) => updateBoost('p1', s, d)}
            onUpdateBase={(s, v) => updateBase('p1', s, v)}
            teraActive={teraActive}
            onClear={() => clearPokemon('p1')}
            onLoadPreset={(savedPokemon) => loadPreset('p1', savedPokemon)}
          />
          <MoveSection
            moves={p1.moves} results={results1}
            onAdd={() => addMove('p1')}
            onUpdate={(i, f, v) => updateMove('p1', i, f, v)}
            onRemove={(i) => removeMove('p1', i)}
            onOptimizeDefense={(i) => optimizeDefense('p1', i)}
            movesList={movesList}
          />
        </Grid>

        {/* Field Conditions — center column */}
        <Grid item xs={12} sm={12} lg={2} order={{ xs: 3, sm: 3, lg: 2 }}>
          <Paper sx={{ p: 1.5, backgroundColor: '#1a1a3e' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#a5b4fc', textAlign: 'center', mb: 1.5, letterSpacing: 1, textTransform: 'uppercase' }}>
              Field
            </Typography>

            {/* Responsive wrapper: vertical on lg (narrow center col), horizontal on sm (full-width row) */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row', lg: 'column' }, gap: { xs: 0, sm: 2, lg: 0 }, flexWrap: 'wrap' }}>

              {/* Weather */}
              <Box sx={{ mb: { xs: 1.5, sm: 0, lg: 1.5 }, flex: { sm: '1 1 auto', lg: 'unset' } }}>
                <Typography sx={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Weather</Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row', lg: 'column' }, flexWrap: 'wrap', gap: 0.4 }}>
                  {WEATHER_OPTIONS.map(w => (
                    <Box key={w} onClick={() => setWeather(w)} sx={fieldToggleSx(weather === w)}>
                      {weatherIcons[w] && React.cloneElement(weatherIcons[w], { sx: { fontSize: 12 } })}
                      {w}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Terrain */}
              <Box sx={{ mb: { xs: 1.5, sm: 0, lg: 1.5 }, flex: { sm: '1 1 auto', lg: 'unset' } }}>
                <Typography sx={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Terrain</Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row', lg: 'column' }, flexWrap: 'wrap', gap: 0.4 }}>
                  {TERRAIN_OPTIONS.map(t => (
                    <Box key={t} onClick={() => setTerrain(t)} sx={fieldToggleSx(terrain === t)}>{t}</Box>
                  ))}
                </Box>
              </Box>

              {/* Global toggles */}
              <Box sx={{ mb: { xs: 1.5, sm: 0, lg: 1.5 }, flex: { sm: '1 1 auto', lg: 'unset' } }}>
                <Typography sx={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Global</Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row', lg: 'column' }, flexWrap: 'wrap', gap: 0.4 }}>
                  {[
                    { label: 'Gravity',     val: gravity,    set: setGravity },
                    { label: 'Magic Room',  val: magicRoom,  set: setMagicRoom },
                    { label: 'Wonder Room', val: wonderRoom, set: setWonderRoom },
                  ].map(({ label, val, set }) => (
                    <Box key={label} onClick={() => set(v => !v)} sx={fieldToggleSx(val)}>{label}</Box>
                  ))}
                </Box>
              </Box>

              {/* Mechanics */}
              <Box sx={{ mb: { xs: 1.5, sm: 0, lg: 1.5 }, flex: { sm: '1 1 auto', lg: 'unset' } }}>
                <Typography sx={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Mechanics</Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row', lg: 'column' }, flexWrap: 'wrap', gap: 0.4 }}>
                  <Box onClick={() => setTeraActive(v => !v)} sx={fieldToggleSx(teraActive)}>Tera</Box>
                </Box>
              </Box>

              {/* Per-side screens */}
              <Box sx={{ flex: { sm: '1 1 auto', lg: 'unset' } }}>
                <Typography sx={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Screens</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.4, mb: 0.25 }}>
                  <Typography sx={{ fontSize: 9, color: '#475569', textAlign: 'center' }}>P1</Typography>
                  <Typography sx={{ fontSize: 9, color: '#475569', textAlign: 'center' }}>P2</Typography>
                </Box>
                {[
                  { key: 'reflect',     label: 'Reflect' },
                  { key: 'lightScreen', label: 'Lt. Screen' },
                  { key: 'auroraVeil',  label: 'Aurora Veil' },
                  { key: 'tailwind',    label: 'Tailwind' },
                ].map(({ key, label }) => (
                  <Box key={key} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.4, mb: 0.4 }}>
                    <Box onClick={() => updatePokemon('p1', key, !p1[key])} sx={fieldToggleSx(p1[key])}>{label}</Box>
                    <Box onClick={() => updatePokemon('p2', key, !p2[key])} sx={fieldToggleSx(p2[key])}>{label}</Box>
                  </Box>
                ))}
              </Box>

            </Box>
          </Paper>
        </Grid>

        {/* Pokémon 2 */}
        <Grid item xs={12} sm={6} lg={5} order={{ xs: 2, sm: 2, lg: 3 }}>
          <PokemonPanel
            label="Pokémon 2"
            pokemon={p2} finalStats={p2Stats} pokemonList={pokemonList} abilitiesList={abilitiesList}
            onUpdate={(f, v) => updatePokemon('p2', f, v)}
            onUpdateSP={(s, d) => updateSP('p2', s, d)}
            onSetSP={(s, v) => setSPDirect('p2', s, v)}
            onUpdateBoost={(s, d) => updateBoost('p2', s, d)}
            onUpdateBase={(s, v) => updateBase('p2', s, v)}
            teraActive={teraActive}
            onClear={() => clearPokemon('p2')}
            onLoadPreset={(savedPokemon) => loadPreset('p2', savedPokemon)}
          />
          <MoveSection
            moves={p2.moves} results={results2}
            onAdd={() => addMove('p2')}
            onUpdate={(i, f, v) => updateMove('p2', i, f, v)}
            onRemove={(i) => removeMove('p2', i)}
            onOptimizeDefense={(i) => optimizeDefense('p2', i)}
            movesList={movesList}
          />
        </Grid>

      </Grid>
    </Box>
  );
};

export default DamageCalculatorPage;
